import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface CollapsibleSectionProps {
  /** Section title, e.g. `Market Rules`, `4 Open Positions`. */
  title: string;
  /** Optional summary chip / inline text shown next to the title (e.g. `Value +$4`). */
  summary?: ReactNode;
  /** Trailing affordance before the chevron (e.g. the Order Book `Limit Order` link). */
  trailing?: ReactNode;
  /** Initial expanded state (admin `defaultOpen`, §7.1). */
  defaultOpen?: boolean;
  /**
   * When true the section has nothing to show — it renders muted and is NOT
   * expandable (§5.4.1 / §5.4.2 / §9). The chevron and toggle are suppressed.
   */
  disabled?: boolean;
  /** Fired whenever the user expands/collapses (analytics §8). */
  onToggle?: (open: boolean) => void;
  /** Section content, rendered only while expanded. */
  children?: ReactNode;
  /** Extra classes for the outer Card (e.g. flattening it into a parent card at lg+). */
  className?: string;
  /** Extra classes for the header row (e.g. desktop row height/padding overrides). */
  headerClassName?: string;
}

/**
 * Generic collapsible market-page section (ARC-82 §5.4).
 *
 * Header with a title, optional summary chip, optional trailing affordance,
 * and a chevron. Expands/collapses on header tap and reports the new state for
 * analytics. When `disabled`, the section renders muted and is not expandable
 * (the nothing-to-show empty state, §9).
 */
export function CollapsibleSection({
  title,
  summary,
  trailing,
  defaultOpen = false,
  disabled = false,
  onToggle,
  children,
  className,
  headerClassName,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen && !disabled);

  function handleToggle() {
    if (disabled) return;
    const next = !open;
    setOpen(next);
    onToggle?.(next);
  }

  return (
    <Card className={cn('flex flex-col', className)}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={cn(
          'flex items-center gap-2 w-full px-4 py-3.5 bg-transparent border-none text-left',
          disabled ? 'cursor-default' : 'cursor-pointer',
          headerClassName,
        )}
      >
        <span className={cn('text-base font-semibold', disabled ? 'text-muted' : 'text-primary')}>{title}</span>
        {summary && <span className="text-sm">{summary}</span>}
        <span className="flex-1" />
        {trailing}
        {!disabled && <ChevronDown className={cn('size-5 text-primary/60 transition-transform', open && 'rotate-180')} />}
      </button>
      {open && !disabled && <div className="px-4 pb-4">{children}</div>}
    </Card>
  );
}
